/* eslint-disable max-len */
import { FC, useEffect, useState, useCallback } from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Formik } from "formik";
import { Form } from "antd";

import SelectAssetsForAltQuote from "./selectAssetsForLiquidtyRequest";
import { resetAssetsPendingCustodyAccount, setAssetsPendingCustodyAccount, setCurrentAssetData, setSelectedAssetsForLiquidity } from "domains/liquidity/store/liquidity.reducer";
import { Button, FormHeader, NavigationButtons, Spinner } from "common/lib/components";
import { formatLiquidityRequestBodyForRequest, formatDocuemtGridDataForRequest } from "domains/liquidity/shared/utils/apiFormatter";
import { LiquidityRequestStatus } from "domains/liquidity/shared/types";
import { AssetType } from "domains/liquidity/shared/constants";
import { DocumentGridStatus } from "domains/documents/shared/types";
import documentsService from "domains/documents/shared/document.api.service";
import { useAssetsHoldingsData } from "domains/altCustody/components/AssetHoldingsTab/useAssetsHoldingsData";
import { useLiquiditySelector, useAdvisorLiquiditySelector } from "domains/liquidity/store/liquidity.selectors";
import { AltCustodyAssetPages } from "domains/altCustody/shared/constants";
import { documentsApiReducer } from "domains/documents/store/documents.api.reducer";
import { liquidityApiReducer, fetchLRAssetsV2 } from "domains/liquidity/store/liquidity.api.reducer";
import { useClientsSelector } from "domains/clients";
import { custodyAccountsApiReducer } from "domains/custodyAccounts/store/custodyAccounts.api.reducer";
import { setCurrentAssets, setPreviousAssets } from "domains/liquidity/store/advisorLiquidity.reducer";
import { CustodyAccountStatus } from "domains/custodyAccounts/shared/types";
import { AccountType } from "common/lib/constants";
import { useAppDispatch } from "store/store.hooks";
import { LAPages } from "domains/liquidity";
import { Select } from "common/lib/formik-antd";

import "./newLiquidtyRequest.scss";

const NewLiquidtyRequest: FC = () => {    
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [ searchParams ] = useSearchParams();

    const { user, isAdvisor, currentClient } = useClientsSelector();

    const { pathname, search, state: locationState } = useLocation()
    const queryParams = new URLSearchParams(search);
    const { investments: assets, loading } = useAssetsHoldingsData()
    const { selectedAssetsForLiquidity, LRAssets, selectedAssetsData } = useLiquiditySelector()
    const { previousAssets, currentAssets } = useAdvisorLiquiditySelector()
      const { data: documents = [], isFetching: isFetchingDocuments } = documentsApiReducer.useFetchDocumentsByIdQuery(user?.account_id ?? skipToken, {
    skip: !user?.account_id,
  });

    const [ reasonForSeekingLiquidity, setReasonForSeekingLiquidity ] = useState(queryParams.get('reason') || "")
    const [ isSubmitting, setIsSubmitting ] = useState(false)
    const [ isFromProposal, setIsFromProposal ] = useState(locationState?.fromProposal || false)
    const [ isLoadingAltQuoteAssets, setIsLoadingAltQuoteAssets ] = useState(false)
    const [ updateLiquidityRequest ] = liquidityApiReducer.useUpdateLiquidityRequestMutation()
    const [ createLRAsset, { isLoading: isCreatingAsset } ] = liquidityApiReducer.useCreateLRAssetMutation({ fixedCacheKey: "create-lr-asset" })
    const [ createLiquidityRequest, { isLoading: isCreatingLR } ] = liquidityApiReducer.useCreateLiquidityRequestMutation({ fixedCacheKey: "create-liquidity-request" })
    const [ confirmAssets, setConfirmAssets ] = useState(queryParams.get('confirmAssets') === "true")
    const { data: custodyAccounts, isFetching: isFetchingCustodyAccounts } = custodyAccountsApiReducer.useFetchCustodyAccountsQuery(
        user ? { accountId: user.account_id, userId: user.user_id } : skipToken,
      );
    const inactiveStates =[ 
        CustodyAccountStatus.Closed,
        CustodyAccountStatus.ClosedForNewAccount,
        CustodyAccountStatus.Duplicate,
        AccountType.CustodialAdvisor,
    ]

    const activeCustodyAccounts = custodyAccounts?.filter(
        (custodyAccount) =>
            custodyAccount.account_status !== CustodyAccountStatus.Closed &&
            custodyAccount.account_status !== CustodyAccountStatus.ClosedForNewAccount &&
            custodyAccount.account_status !== CustodyAccountStatus.Duplicate &&
            custodyAccount.account_type !== AccountType.CustodialAdvisor,
    );

    const isAltQuote = pathname.includes('altquote')
    const reasonsForSeekingLiquidity = [
        { label: "Life Circumstance" },
        { label: "Business Need" },
        { label: "Portfolio Rebalancing" },
        { label: "Tax Motivation" },
        { label: "Regulatory/Compliance" },
        { label: "GP Restructuring" },
        { label: "Other" },
    ];

    const connectAssets = async (assetsToCreate: any, LRId: string) => {
        const isAltquote = pathname.includes('altquote');
        const changes: any = { }
        if (isAltquote) {
            changes.reason_for_seeking_liquidity = reasonForSeekingLiquidity
            changes.status = 'alt_quote_submitted'
        } else {
            changes.reason_for_seeking_liquidity = reasonForSeekingLiquidity
            changes.status = documents.length ? 'in_document_review' : 'pending_asset_statements'
        }
        
        if (assetsToCreate) {
            for (const asset of assetsToCreate) {
                try {
                    const assetValues = { ...asset };
                    dispatch(setCurrentAssetData(assetValues)); 
                    dispatch(setAssetsPendingCustodyAccount(assetValues.asset_id))

                    const createdAsset = await createLRAsset(LRId)
                    
                    if (createdAsset?.data && user && (asset.asset_type === AssetType.Fund || asset.asset_type === AssetType.PreqinFund)) {
                        const assetWithId = { ...asset, asset_id: createdAsset.data.asset_id };
                        
                        const assetDocuments = documents.filter(doc => doc.asset_id === asset.asset_id);
                        const hasDocuments = assetDocuments.length > 0;
                        
                        const documentStatus = hasDocuments 
                            ? DocumentGridStatus.PendingReview 
                            : DocumentGridStatus.NotReceived;
                        
                        const bodyForRequest = formatDocuemtGridDataForRequest(
                            assetWithId,
                            user,
                            LRId,
                            documentStatus,
                        );
                        bodyForRequest.liquidity_request_id = '';
                        bodyForRequest.is_append_row = false;
                        
                        await documentsService.setDocumentGridDataA(user.account_id, bodyForRequest);
                    }
                } catch (error) {
                    console.error("Error creating asset:", error);
                }
            }
            
            try {
                await updateLiquidityRequest({ LRId, changes })
            } catch (error) {
                console.error("Error updating liquidity request:", error);
            }
        }
    };

    const filterAssetsByInactiveCustodyStatus = (
        assetsToCreate: any[], 
        custodyAccounts: any[], 
        inactiveStates: string[],
      ) => {
        return assetsToCreate?.filter(asset => {
          const custodyAccount = custodyAccounts.find(ca => ca.account_id === asset.custody_account_id);
          return custodyAccount && inactiveStates.includes(custodyAccount.account_status);
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const isAltquote = pathname.includes('altquote');
            const applicationType = isAltquote ? 'alt_quote' : 'liquidity_request'

            if (user) {
                formatLiquidityRequestBodyForRequest(applicationType, user)
                const currentPath = location.pathname;
                const pathParts = currentPath.split('/');
                let newPath = ''
                            
                const assetsToCreate: any = selectedAssetsData?.length 
                    ? selectedAssetsData 
                    : assets?.filter(({ id }) => id && selectedAssetsForLiquidity?.some((astId:any) => astId === id));

                if (!isAltquote && !isAdvisor) {
                    if (!custodyAccounts || !activeCustodyAccounts?.length) {
                        dispatch(resetAssetsPendingCustodyAccount())
                        for (const asset of assetsToCreate) {
                            dispatch(setAssetsPendingCustodyAccount(asset.asset_id))
                        }
                        newPath = `${ pathParts.join('/') }/${ LAPages.CustodyAccountAgreement }`;
                        navigate(newPath, { state: { fromLiquidityRequestWithExpiredCAs: assetsToCreate.map(({ asset_id }:any) => asset_id), reasonForSeekingLiquidity  } });
                        return;
                    }
                    
                    const filteredAssets = filterAssetsByInactiveCustodyStatus(assetsToCreate, custodyAccounts, inactiveStates);
                    if (filteredAssets.length) {
                        newPath = `${ pathParts.join('/') }/${ LAPages.CustodyAccount }`;
                        navigate(newPath, { state: { fromLiquidityRequestWithExpiredCAs: filteredAssets.map(({ asset_id }) => asset_id ), reasonForSeekingLiquidity } });
                        return;
                    }
                }

                const overrideData = !isAltquote 
                    ? {
                        status: LiquidityRequestStatus.PendingSubmission,
                    } : undefined 
                let LRId
                
                if (isAdvisor) {
                    LRId = await createLiquidityRequest({ isLA: !isAltquote, overrideData, client: currentClient ?? undefined });
                } else {
                    LRId = await createLiquidityRequest({ isLA: !isAltquote, overrideData });
                }
                
                if (LRId.data) {
                    await connectAssets(assetsToCreate, LRId.data)
                    
                    if (isAdvisor) {
                        if (!isAltquote) {
                            if(custodyAccounts && activeCustodyAccounts?.length) {
                                navigate(`.${ AltCustodyAssetPages.CustodyAccount  }`, { state: { reasonForSeekingLiquidity } })
                            } else {
                                navigate(`${ LAPages.CustodyAccountAgreement  }`, { state: { reasonForSeekingLiquidity } })
                            }
                        } else {
                            navigate(`/my-applications/${ LRId.data }/altquote`)
                        }
                    } else {
                        if (!isAltquote) {
                            navigate(`.${ AltCustodyAssetPages.ThankYouApplication  }`)
                        } else {
                            navigate(`/my-applications/${ LRId.data }/altquote`)
                        }
                    }
                }
            }
        } catch (error) {
            console.error(error);
            setIsSubmitting(false);
        }
    }

    const handleSelect = (value: any) => {
        if (value !== 'undefined') {
            setReasonForSeekingLiquidity(value)
        }
    }

    function findDifference(arr1: any[], arr2: any[]): any[] {
        const idsInArr2 = new Set(arr2.map(item => item.id));
        return arr1.filter(item => !idsInArr2.has(item.id));
    }

    useEffect(() => {
        if (!loading) {
            if (!assets?.length) {
                navigate('./select_assets')
            } else if (isAdvisor) {
                if (previousAssets === null) {
                    if (isFromProposal && LRAssets?.length) {
                        // Handle fromProposal for advisors - use LRAssets instead of all assets
                        const includedAssets = []
                        for (let index = 0; index < LRAssets.length; index++) {
                            const element = LRAssets[ index ];
                            const matchAsset = assets.find(({ asset_id }) => {
                                return asset_id === element.asset_id
                            })
                            if (matchAsset) {
                                includedAssets.push(matchAsset)
                            }
                        }

                        dispatch(setPreviousAssets([]))
                        dispatch(setCurrentAssets(includedAssets))
                    } else if (selectedAssetsForLiquidity && isFromProposal) {
                        if (selectedAssetsData?.length) {
                            dispatch(setPreviousAssets([]))
                            dispatch(setCurrentAssets(selectedAssetsData))
                        }
                    } else {
                        dispatch(setPreviousAssets(assets))
                        navigate('./select_assets')
                    }
                } else {
                    const diff = findDifference(assets, previousAssets)
                    if (!diff.length) {
                        navigate('./select_assets')
                    }
                    dispatch(setCurrentAssets(diff))
                }
            } else {
                // Handle fromProposal for non-advisors
                if (isFromProposal && LRAssets?.length) {
                    // Pre-select assets from LRAssets for non-advisors
                    const assetIds = LRAssets.map((asset: any) => {
                        const matchAsset = assets.find(({ asset_id }) => asset_id === asset.asset_id)
                        if (matchAsset) {
                            return matchAsset.id
                        }
                        return null
                    }).filter(Boolean)
                    
                    if (assetIds.length) {
                        dispatch(setSelectedAssetsForLiquidity(assetIds))
                    }
                } else {
                    // Normal flow for non-advisors without fromProposal
                }
            }
        }
    }, [ loading, selectedAssetsData, LRAssets, isFromProposal ])

    useEffect(() => {
        if (isAdvisor && assets && !assets.length) {
            dispatch(setPreviousAssets([]))
        }
    }, [ assets ])
        
    useEffect(() => {
        if (!selectedAssetsForLiquidity.length && confirmAssets) {
            setConfirmAssets(false)
        }
    }, [ selectedAssetsForLiquidity ])

    useEffect(() => {
        if (reasonForSeekingLiquidity) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("reason", reasonForSeekingLiquidity)
            navigate(`${ location.pathname }?${ searchParams.toString() }`, { replace: true });
        }
    }, [ reasonForSeekingLiquidity ])

    // Handle fetchLRAssetsV2 when coming from formal proposal request
    useEffect(() => {
        if (locationState?.fromProposal && locationState?.altQuoteId) {
            setIsFromProposal(true);
            setIsLoadingAltQuoteAssets(true);
            dispatch(fetchLRAssetsV2(locationState.altQuoteId));
        }
    }, [ locationState?.fromProposal, locationState?.altQuoteId, dispatch ])

    // Hide loading spinner when LRAssets are loaded
    useEffect(() => {
        if (isLoadingAltQuoteAssets && LRAssets?.length > 0) {
            setIsLoadingAltQuoteAssets(false);
        }
    }, [ isLoadingAltQuoteAssets, LRAssets ])

    useEffect(() => {
        const submitParam = searchParams.get("submit");
        
        if (submitParam === 'true' && 
            selectedAssetsForLiquidity?.length > 0 &&
            reasonForSeekingLiquidity) {
            
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('submit');
            const newUrl = `${ window.location.pathname }?${ newSearchParams.toString() }`;
            window.history.replaceState({}, '', newUrl);
            
            handleSubmit();
        }
    }, [ searchParams.get("submit"), selectedAssetsForLiquidity, reasonForSeekingLiquidity ])

    useEffect(() => {
        const formalProposalRequestedParam = searchParams.get("formalProposalRequested");
        
        if (formalProposalRequestedParam) {
            const resetIsFormalProposalRequested = async () => {
                try {
                    await updateLiquidityRequest({ 
                        LRId: formalProposalRequestedParam, 
                        changes: { 
                            is_formal_proposal_requested: false,
                        },
                    });
                } catch (error) {
                    console.error("Error resetting is_formal_proposal_requested:", error);
                }
            };
            
            resetIsFormalProposalRequested();
            
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('formalProposalRequested');
            const newUrl = `${ window.location.pathname }?${ newSearchParams.toString() }`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    const handleSetSelectedAssets = useCallback((assetIds: string[]) => {
        dispatch(setSelectedAssetsForLiquidity(assetIds));
    }, [ dispatch ]);

    const isLoading = loading || isFetchingDocuments || isFetchingCustodyAccounts || isCreatingLR || isCreatingAsset || isSubmitting;

    if (isAdvisor && !currentAssets.length) {
        return <Spinner />
    }

    return (
        <Spinner spinning={ isLoading }>
            <div className="ben-main-form-wrapper">
                <FormHeader
                    subtitle={ `Confirm Assets for ${ isAltQuote ? "AltQuote" : "Liquidity Request" }` }
                    title="Assets to Exchange"
                    hint="Review Assets Info"
                />
                <div className="ben-form-container" style={ { gap: 32 } }>
                    {(() => {
                        if (!isAdvisor) {
                            // Show loading spinner when coming from proposal and assets are being loaded
                            if (isFromProposal && isLoadingAltQuoteAssets) {
                                return <Spinner />
                            }
                            
                            if (!assets?.length) {
                                return (
                                    <div className="ben-no-assets">
                                        <span>
                                            Please, add assets to your {isAltQuote ? "AltQuote" : "Liquidity Request"}
                                        </span>
                                        <Button onClick={ () => navigate('./select_assets') }>+ New Asset</Button>
                                    </div>
                                );
                            } else {
                                // For non-advisors, always show all assets
                                const assetsToShow = assets;
                                
                                return (
                                    <SelectAssetsForAltQuote
                                        selectedAssets={ selectedAssetsForLiquidity }
                                        setSelectedAssets={ handleSetSelectedAssets }
                                        investments={ assetsToShow }
                                        fromProposal={ isFromProposal }
                                    />
                                );
                            }
                        } else {
                            // Show loading spinner when coming from proposal and assets are being loaded
                            if (isFromProposal && isLoadingAltQuoteAssets) {
                                return <Spinner />
                            }
                            
                            if (!currentAssets?.length) {
                                return (
                                    <div className="ben-no-assets">
                                        <span>
                                            Please, add assets to your {isAltQuote ? "AltQuote" : "Liquidity Request"}
                                        </span>
                                        <Button onClick={ () => navigate('./select_assets') }>+ New Asset</Button>
                                    </div>
                                );
                            } else {
                                return (
                                    <SelectAssetsForAltQuote
                                        selectedAssets={ selectedAssetsForLiquidity }
                                        setSelectedAssets={ handleSetSelectedAssets }
                                        investments={ currentAssets }
                                        fromProposal={ isFromProposal }
                                    />
                                );
                            }
                        }
                    })()}

                    <Formik
                        initialValues={ { reason_for_seeking_liquidity: '' } }
                        validateOnMount
                        validateOnChange
                        onSubmit={ handleSubmit }
                    >
                        <Form layout="vertical">
                            <Form.Item
                                className="ben-input"
                                label="Select Reason for Seeking Liquidity"
                                name="reason_for_seeking_liquidity"
                            >
                                <Select
                                    onSelect={ handleSelect }
                                    initialValue={ reasonForSeekingLiquidity }
                                    placeholder="Select Reason for Seeking Liquidity"
                                    name="reason_for_seeking_liquidity"
                                >
                                    {reasonsForSeekingLiquidity.map((reason) => (
                                        <Select.Option key={ reason.label } value={ reason.label }>
                                            {reason.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <NavigationButtons
                                disabled={ !selectedAssetsForLiquidity?.length || !reasonForSeekingLiquidity || isLoading }
                                submitButtonText="Submit"
                                backButtonText={ `+ Add Another Asset` }
                                onNext={ handleSubmit }
                                onBack={ () => navigate('./select_assets') }
                            />
                        </Form>
                    </Formik>
                </div>
            </div>
        </Spinner>
    );
}

export default NewLiquidtyRequest;
